import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { Flex, RemoteImage, Typography, ColorValues } from '@banhala/ui-kit';
import type { PhotoReviewItem } from '../types';
import type { AnyObject } from '../../../../../types';
import { bridge } from '../../../../../utils';

const CARD_WIDTH = 264;
const REVIEW_IMAGE_HEIGHT = 160;
const REVIEW_IMAGE_BORDER_RADIUS = 6;
const CARD_BORDER_RADIUS = 8;
const GOODS_IMAGE_SIZE = 40;
const GOODS_IMAGE_BORDER_RADIUS = 4;

interface PhotoReviewCardProps {
  item: PhotoReviewItem;
  logging: AnyObject;
}

export const PhotoReviewCard = ({ item, logging }: PhotoReviewCardProps) => {
  const {
    review_thumbnail,
    review_contents,
    review_deeplink,
    goods_thumbnail,
    goods_name,
    goods_deeplink,
    market_name,
  } = item;

  const handleReviewPress = useCallback(() => {
    if (review_deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_REVIEW',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      bridge.openDeeplink(review_deeplink);
    }
  }, [review_deeplink, logging]);

  const handleGoodsPress = useCallback(() => {
    if (goods_deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_GOODS',
        analytics: logging.analytics,
        ably_only_log: { ...logging.ably_only_log, event_name: 'click' },
      });
      bridge.openDeeplink(goods_deeplink);
    }
  }, [goods_deeplink, logging]);

  return (
    <Flex padding={{ top: 4, bottom: 8, horizontal: 2 }}>
      <Flex
        width={CARD_WIDTH}
        borderRadius={CARD_BORDER_RADIUS}
        overflow="hidden"
        backgroundColor="bg_always_white"
        shadowStep="medium"
      >
          {/* Review area */}
          <Flex padding={{ horizontal: 12 }} onPress={handleReviewPress}>
            <Flex padding={{ top: 12 }} gap={12}>
              <Flex
                borderRadius={REVIEW_IMAGE_BORDER_RADIUS}
                overflow="hidden"
              >
                <RemoteImage
                  uri={review_thumbnail}
                  width={CARD_WIDTH - 24}
                  height={REVIEW_IMAGE_HEIGHT}
                />
              </Flex>
              <Flex minHeight={32}>
                <Typography
                  type="body3"
                  color="content_primary"
                  numberOfLines={2}
                >
                  {review_contents}
                </Typography>
              </Flex>
            </Flex>
          </Flex>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Goods area */}
          <Flex
            padding={{ horizontal: 12, bottom: 12 }}
            onPress={handleGoodsPress}
          >
            <Flex.Row alignItems="center" gap={8} padding={{ vertical: 8 }}>
              <Flex
                width={GOODS_IMAGE_SIZE}
                height={GOODS_IMAGE_SIZE}
                borderRadius={GOODS_IMAGE_BORDER_RADIUS}
                overflow="hidden"
              >
                <RemoteImage
                  uri={goods_thumbnail}
                  width={GOODS_IMAGE_SIZE}
                  height={GOODS_IMAGE_SIZE}
                />
              </Flex>
              <Flex flex={1} gap={2}>
                <Typography
                  type="body4"
                  color="content_secondary"
                  numberOfLines={1}
                >
                  {market_name}
                </Typography>
                <Typography
                  type="body4"
                  color="content_primary"
                  numberOfLines={1}
                >
                  {goods_name}
                </Typography>
              </Flex>
            </Flex.Row>
          </Flex>
      </Flex>
    </Flex>
  );
};

const styles = StyleSheet.create({
  divider: {
    height: 1,
    backgroundColor: ColorValues.gray20,
    marginHorizontal: 12,
    marginVertical: 12,
  },
});
